import type { Contact, ContactField } from '../api/types.js';
import { sortByOwnerOrder } from './field-order.js';
import { createdAt, lastChangedAt } from '../lib/contact-timestamps.js';

// A merged person = the union of every subscription I hold to that same owner
// (grouped by owner_id). See docs/CONTACT-SYNC.md "Merged contact view".
export interface MergedContact {
  ownerId: string;
  displayName: string;
  // The owner's username/handle (for the `virtualid.one/id/<handle>` link), if known.
  handle?: string;
  // Given/family names derived from the union of synced fields (for the friendly title).
  givenName?: string;
  familyName?: string;
  company?: string;
  // All my subscriptions to this person, in original order (source of truth for
  // Refresh / Sync-all / notes / labels, which stay per-subscription).
  subscriptions: Contact[];
  // De-duplicated union of fields across those subscriptions.
  fields: ContactField[];
  // Profile names of the owner's profiles I'm subscribed to (badges).
  profiles: { id: string; name: string; disabled: boolean; revoked: boolean; isPublic: boolean }[];
  // Privacy-safe summary of granted profiles (NEVER exposes profile names).
  // publicDisabled: the public profile subscription exists and is disabled.
  //   Rule: true iff some subscription has is_public && sync_state==='profile_disabled'.
  //   If no public subscription is present at all, we default to false (plain "Public").
  publicDisabled: boolean;
  // extraProfileCount: count of non-revoked subscribed profiles that are NOT the
  // public one. Revoked profiles are excluded. Drives the "+N" pill.
  extraProfileCount: number;
  // Union of labels across subscriptions (for search + pills).
  labels: string[];
  // A person is "live" if ANY of their subscriptions is live (not all disabled).
  live: boolean;
  // Every subscription is a disabled profile.
  allDisabled: boolean;
  // Every subscription has been revoked by its owner (no longer shared).
  allRevoked: boolean;
  // ANY subscription is a tentative explicit-approval request awaiting the owner
  // (grayed, "Pending Approval"). Mutually visible with allRevoked=false.
  pendingApproval: boolean;
  // Created = earliest added_at across merged subscriptions (ISO), if known.
  createdAt?: string;
  // Last Changed = latest content_changed_at across merged subscriptions (ISO).
  lastChangedAt?: string;
}

// mergeContactsByOwner groups the flat contact list (one row per subscription)
// into ONE card per person.
//
// Field de-dup rule: key by ContactField.key (the owner field id, `fld_…`). When
// the same field appears in several subscriptions, PREFER a syncing=true copy
// over a syncing=false one; a field is only "not syncing" (gray) when syncing is
// false in ALL subscriptions that carry it.
export function mergeContactsByOwner(contacts: Contact[]): MergedContact[] {
  const order: string[] = [];
  const groups = new Map<string, Contact[]>();
  for (const c of contacts) {
    const key = c.owner_id || c.subscription_id; // fall back so ungrouped still render
    if (!groups.has(key)) { groups.set(key, []); order.push(key); }
    groups.get(key)!.push(c);
  }

  return order.map((ownerId) => {
    const subs = groups.get(ownerId)!;
    const fieldsByKey = new Map<string, ContactField>();
    for (const c of subs) {
      for (const f of c.fields || []) {
        const existing = fieldsByKey.get(f.key);
        if (!existing) { fieldsByKey.set(f.key, { ...f }); continue; }
        // Prefer a syncing copy; if the new one is syncing and the kept one is not,
        // replace (adopt its fresh value + syncing=true).
        if (f.syncing && !existing.syncing) fieldsByKey.set(f.key, { ...f });
      }
    }
    const profiles = subs.map((c) => ({
      id: c.profile_id || '',
      name: c.profile_name || 'Profile',
      disabled: c.sync_state === 'profile_disabled',
      revoked: c.sync_state === 'revoked' || c.status === 'revoked',
      isPublic: c.is_public === true,
    }));
    // Privacy-safe summary (no names). publicDisabled: a public subscription
    // exists and is disabled. extraProfileCount: non-revoked, non-public count.
    const publicDisabled = profiles.some((p) => p.isPublic && p.disabled);
    const extraProfileCount = profiles.filter((p) => !p.revoked && !p.isPublic).length;
    const labels = [...new Set(subs.flatMap((c) => c.labels || []))];
    const first = subs[0];
    const isRevoked = (c: Contact): boolean => c.sync_state === 'revoked' || c.status === 'revoked';
    const allDisabled = subs.every((c) => c.sync_state === 'profile_disabled');
    const allRevoked = subs.every(isRevoked);
    const mergedFields = sortByOwnerOrder([...fieldsByKey.values()]);
    const findVal = (k: string): string | undefined =>
      mergedFields.find((f) => f.type === k || f.key === k)?.value?.trim() || undefined;
    return {
      ownerId,
      displayName: first.owner_handle || subs.map((c) => c.owner_handle).find(Boolean) || first.display_name || first.owner_name || 'Contact',
      handle: subs.map((c) => c.owner_handle).find(Boolean) || undefined,
      givenName: findVal('given_name'),
      familyName: findVal('family_name'),
      company: subs.map((c) => c.company).find(Boolean),
      subscriptions: subs,
      fields: mergedFields,
      profiles,
      publicDisabled,
      extraProfileCount,
      labels,
      live: subs.some((c) => c.live && c.sync_state !== 'profile_disabled' && !isRevoked(c)),
      allDisabled,
      allRevoked,
      pendingApproval: subs.some((c) => c.pending_approval === true),
      createdAt: createdAt(subs),
      lastChangedAt: lastChangedAt(subs),
    };
  });
}
